import {
  differenceInDays,
  differenceInMonths,
  differenceInWeeks,
  differenceInYears,
  intlFormatDistance,
} from "date-fns";
import { detectInitialLanguage } from "localization/localization";
import { IntegrationSlotStatusEnum } from "types/AutomatedCollection";
import { BadgeColor } from "ui/core/Badge";

import { capitalizeFirstLetter } from "utilities/utils";

export const IntegrationSlotStatusColors: Record<
  IntegrationSlotStatusEnum,
  BadgeColor
> = {
  "Emissions ready": "success",
  "Awaiting data": "gray",
  "Partially complete": "warning",
  Processing: "warning",
  Calculating: "warning",
  "Collection failed": "error",
  Upcoming: "initial",
};

export const getIntegrationSlotStatusTooltip = (
  translate: (key: string) => string
): Record<IntegrationSlotStatusEnum, string> => ({
  "Emissions ready": translate("emissionsReady"),
  "Awaiting data": translate("awaitingData"),
  "Partially complete": translate("partiallyComplete"),
  Processing: translate("processingAndCalculating"),
  "Collection failed": translate("collectionFailed"),
  Calculating: translate("processingAndCalculating"),
  Upcoming: translate("awaitingData"),
});

export const dateString = (dateString?: string | null): string => {
  if (!dateString) {
    return "Never";
  }

  const date = new Date(dateString);
  return date.toLocaleDateString(detectInitialLanguage(), {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export const dateDistance = (
  dateString: string | Date | null,
  start?: Date,
  capitalize = true
): string => {
  if (!dateString) {
    return "Never";
  }

  const endDate = new Date(dateString);
  const startDate = start ?? new Date();

  const unit = (() => {
    switch (true) {
      case Math.abs(differenceInYears(endDate, startDate)) >= 1:
      case Math.abs(differenceInMonths(endDate, startDate)) >= 12:
        return "year";

      case Math.abs(differenceInMonths(endDate, startDate)) >= 1:
      case Math.abs(differenceInWeeks(endDate, startDate)) >= 4:
        return "month";

      case Math.abs(differenceInWeeks(endDate, startDate)) >= 1:
      case Math.abs(differenceInDays(endDate, startDate)) >= 7:
        return "week";

      default:
        return "day";
    }
  })();

  const result = intlFormatDistance(endDate, startDate, {
    locale: detectInitialLanguage(),
    unit,
  });

  return capitalize ? capitalizeFirstLetter(result) : result;
};
